import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import PageWrapper from '../components/PageWrapper';
import PageWrapperAuto from '../components/PageWrapperAuto';
import {
  collection,
  onSnapshot,
  updateDoc,
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  serverTimestamp
} from 'firebase/firestore';
import { useAuth } from '../AuthProvider';
import { useUI } from '../UIContext';
import { ACTIVE_SESSION_STORAGE_KEY, useActiveSession } from '../ActiveSessionContext';
import { normalizeKey, normalizeType, normalizeGenre, normalizeTagsArray, normalizeTag, keyRoot } from '../utils/normalize';
import BorderedBox from '../components/BorderedBox';
import TuneCardBox from '../components/TuneCardBox';
import StyledCheckbox from '../components/StyledCheckbox';
import myIcon from '../assets/logo512.png'; // adjust path
import dexie from '../offlineDB';
import { addPendingWrite, flushPendingWrites } from '../utils/flushPendingWrites';
import { useUserPrefs } from '../UserPrefsProvider';
import TagFilterControl from '../components/TagFilterControl';
import CustomTagInput from '../components/CustomTagInput';
import ActionConfirmModal from '../components/ActionConfirmModal';
import { getModeLabelsForEntity } from '../utils/modeLabels';
import ModeSwitcher from '../components/ModeSwitcher';
import { FaQuestionCircle } from 'react-icons/fa';
import AppSelect from '../components/AppSelect';
import TimeFilterPanel from '../components/TimeFilterPanel';
import UndoBanner from '../components/UndoBanner';
import SwipeablePinnableRow from '../components/SwipeablePinnableRow';
import SendExportModal from '../components/SendExportModal';
import ExportModal from '../components/ExportModal';
import { getUserProfile, sendSessionToUser, sendBatchToUser } from '../utils/socialFirestore';
import InlineEditableTitle from '../components/InlineEditableTitle';
import DetailTagEditor from '../components/DetailTagEditor';
import DetailOrganizeReorderPanel from '../components/DetailOrganizeReorderPanel';
import PreviewModePill from '../components/PreviewModePill';
import DetailTimestampsFooter from '../components/DetailTimestampsFooter';

function toSessionDate(value) {
  if (value instanceof Date) return value;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (typeof value === 'string' || typeof value === 'number') return new Date(value);
  return null;
}

function formatDateTimeNoSeconds(value) {
  const date = toSessionDate(value);
  if (!date || Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function normalizeSessionSegments(session) {
  const raw = Array.isArray(session?.segments) ? session.segments : [];
  const normalized = raw
    .map((segment) => ({
      startedAt: toSessionDate(segment?.startedAt),
      endedAt: toSessionDate(segment?.endedAt),
    }))
    .filter((segment) => segment.startedAt && !Number.isNaN(segment.startedAt.getTime()));
  if (normalized.length > 0) return normalized;
  const legacyStart = toSessionDate(session?.startedAt);
  if (!legacyStart || Number.isNaN(legacyStart.getTime())) return [];
  return [{ startedAt: legacyStart, endedAt: toSessionDate(session?.endedAt) }];
}

function getSessionTotalOpenMinutes(session, nowTs = Date.now()) {
  const segments = normalizeSessionSegments(session);
  if (segments.length === 0) return 0;
  let totalMs = 0;
  segments.forEach((segment) => {
    const startTs = segment.startedAt?.getTime?.();
    if (!startTs || Number.isNaN(startTs)) return;
    const endTs = segment.endedAt?.getTime?.() || nowTs;
    if (!endTs || Number.isNaN(endTs)) return;
    if (endTs > startTs) totalMs += (endTs - startTs);
  });
  return Math.max(0, Math.floor(totalMs / 60000));
}

function formatDurationLabel(totalMinutes) {
  if (!totalMinutes || totalMinutes < 1) return '0 min';
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const parts = [
    hours > 0 ? `${hours} hr${hours > 1 ? 's' : ''}` : null,
    minutes > 0 ? `${minutes} min` : null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : '0 min';
}

function getSessionTimeFieldDate(session, basis) {
  if (!session) return null;
  if (basis === 'viewed') return toSessionDate(session.lastViewedAt);
  if (basis === 'created') return toSessionDate(session.createdAt) || toSessionDate(session.startedAt);
  return (
    toSessionDate(session.updatedAt) ||
    toSessionDate(session.endedAt) ||
    toSessionDate(session.startedAt) ||
    toSessionDate(session.createdAt)
  );
}

function matchesSessionTimeRange(session, range, basis, selectedMonth, selectedYear) {
  if (!range) return true;
  const date = getSessionTimeFieldDate(session, basis);
  if (!date || isNaN(date.getTime())) return false;

  const now = new Date();
  if (range === 'past-week') {
    const start = new Date(now);
    start.setDate(start.getDate() - 7);
    return date >= start && date <= now;
  }
  if (range === 'this-month') {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    return date >= start && date <= now;
  }
  if (range === 'past-month') {
    const start = new Date(now);
    start.setDate(start.getDate() - 30);
    return date >= start && date <= now;
  }
  if (range === 'past-year') {
    const start = new Date(now);
    start.setDate(start.getDate() - 365);
    return date >= start && date <= now;
  }
  if (range === 'year') {
    const year = Number(selectedYear);
    if (!year) return true;
    return date.getFullYear() === year;
  }
  if (range === 'month') {
    if (!selectedMonth) return true;
    const [y, m] = String(selectedMonth).split('-').map(Number);
    if (!y || !m) return true;
    return date.getFullYear() === y && (date.getMonth() + 1) === m;
  }
  return true;
}

function hydrateSessions(sessionDocs, allTunes, allSets) {
  return sessionDocs.map((session) => {
    const tunesInSession = allTunes.filter((tune) =>
      Array.isArray(tune.sessions) &&
      tune.sessions.some((s) => s.sessionId === session.id)
    ).map((tune) => ({ ...tune, _entryType: 'tune' }));
    const setsInSession = allSets.filter((set) =>
      Array.isArray(set.sessions) &&
      set.sessions.some((s) => s.sessionId === session.id)
    ).map((set) => ({ ...set, _entryType: 'set' }));

    return {
      id: session.id,
      location: session.location,
      mode: session.mode || 'tunes',
      pinned: session.pinned || false,
      customTags: Array.isArray(session.customTags) ? session.customTags : [],
      notes: session.notes || '',
      createdAt: toSessionDate(session.createdAt),
      updatedAt: toSessionDate(session.updatedAt),
      lastViewedAt: toSessionDate(session.lastViewedAt),
      startedAt: toSessionDate(session.startedAt),
      endedAt: toSessionDate(session.endedAt),
      segments: normalizeSessionSegments(session),
      tunes: [...tunesInSession, ...setsInSession]
    };
  });
}

const SessionViewer = () => {
  const [tunes, setTunes] = useState([]);
  const [sets, setSets] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedSessionKeys, setSelectedSessionKeys] = useState([]);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [undoTimer, setUndoTimer] = useState(null);
  const [swipeSendTarget, setSwipeSendTarget] = useState(null); // session being sent via swipe
  const [showBulkSendPicker, setShowBulkSendPicker] = useState(false);
  const [showBulkExportModal, setShowBulkExportModal] = useState(false);
  const [confirmDeleteSession, setConfirmDeleteSession] = useState(false);
  const { user } = useAuth();
  const { inputStyle, textStyle } = useUI();
  const { activeSession, setActiveSession } = useActiveSession();
  const navigate = useNavigate();
const [filterQuery, setFilterQuery] = useState('');
const [filterGenre, setFilterGenre] = useState('');
const [selectedTags, setSelectedTags] = useState([]);
const [tagLogic, setTagLogic] = useState('AND');
const [sortOrder, setSortOrder] = useState('newest');
const [showTagFilterUI, setShowTagFilterUI] = useState(false);
const [filterTimeRange, setFilterTimeRange] = useState('');
const [filterTimeBasis, setFilterTimeBasis] = useState('updated');
const [filterTimeMonth, setFilterTimeMonth] = useState('');
const [filterTimeYear, setFilterTimeYear] = useState('');
const [showTimeFilters, setShowTimeFilters] = useState(false);
const [monthPickerYear, setMonthPickerYear] = useState(new Date().getFullYear());
const [bulkTagInputVisible, setBulkTagInputVisible] = useState(false);
const [bulkTags, setBulkTags] = useState([]);
const [bulkActionValue, setBulkActionValue] = useState('');
const [notice, setNotice] = useState(null);
const [showEndSessionModal, setShowEndSessionModal] = useState(false);
const [showDeleteSelectedModal, setShowDeleteSelectedModal] = useState(false);
const resultsContainerRef = useRef(null);
const [scrollAreaHeight, setScrollAreaHeight] = useState(320);
const updateScrollHeight = React.useCallback(() => {
  if (!resultsContainerRef.current) return;
  const rect = resultsContainerRef.current.getBoundingClientRect();
  setScrollAreaHeight(window.innerHeight - rect.top - 8);
}, []);
useEffect(() => {
  updateScrollHeight();
  window.addEventListener('resize', updateScrollHeight);
  return () => window.removeEventListener('resize', updateScrollHeight);
}, [updateScrollHeight]);
const [isCompactActions, setIsCompactActions] = useState(() => (
  typeof window !== 'undefined' ? window.innerWidth <= 560 : false
));
const [isEditingSessionDetail, setIsEditingSessionDetail] = useState(false);
const [showSessionOrganizer, setShowSessionOrganizer] = useState(false);
const [sessionDraft, setSessionDraft] = useState({
  location: '',
  notes: '',
  startedAt: '',
  endedAt: '',
  customTags: [],
  items: [],
});
const [sessionItemSearchAny, setSessionItemSearchAny] = useState('');
const [sessionItemFilterGenre, setSessionItemFilterGenre] = useState('');
const [sessionItemFilterType, setSessionItemFilterType] = useState('');
const [sessionItemFilterKey, setSessionItemFilterKey] = useState('');
const [sessionItemTypeFilter, setSessionItemTypeFilter] = useState('all');
const [isEditingSessionTitle, setIsEditingSessionTitle] = useState(false);
const [editSessionTitleValue, setEditSessionTitleValue] = useState('');
const [sessionDragIndex, setSessionDragIndex] = useState(null);
const sessionNotesTextareaRef = useRef(null);

const { prefs, labels } = useUserPrefs();
const [locationInput, setLocationInput] = useState('');

const currentMode = prefs?.appMode || 'tunes';
const getCollectionBadge = (entityMode) => (
  (getModeLabelsForEntity(currentMode, entityMode || 'tunes')?.Collection || 'Set').toUpperCase()
);
const getSessionModeTagLabel = (entityMode) => (
  (entityMode || 'tunes') === 'notes' ? 'Notes Session' : 'Tunes Session'
);
const showNotice = (text, tone = 'info') => {
  setNotice({ text, tone });
};

useEffect(() => {
  if (!notice) return undefined;
  const timer = setTimeout(() => setNotice(null), 2600);
  return () => clearTimeout(timer);
}, [notice]);

const toInputFormat = (val) => {
  let date;
  if (val?.seconds != null) {
    date = new Date(val.seconds * 1000);
  } else if (val?.toDate) {
    date = val.toDate();
  } else {
    date = new Date(val);
  }
  if (isNaN(date)) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

useEffect(() => {
  if (!selectedSession) return;
  setSessionDraft({
    location: selectedSession.location || '',
    notes: selectedSession.notes || '',
    startedAt: toInputFormat(selectedSession.startedAt),
    endedAt: toInputFormat(selectedSession.endedAt),
    customTags: Array.isArray(selectedSession.customTags) ? selectedSession.customTags : [],
    items: Array.isArray(selectedSession.tunes) ? selectedSession.tunes.map((item) => ({ ...item })) : [],
  });
  setIsEditingSessionDetail(false);
  setShowSessionOrganizer(false);
  setSessionItemSearchAny('');
  setSessionItemFilterGenre('');
  setSessionItemFilterType('');
  setSessionItemFilterKey('');
  setSessionItemTypeFilter('all');
  setEditSessionTitleValue(selectedSession.location || '');
}, [selectedSession]);


useEffect(() => {
  const onResize = () => setIsCompactActions(window.innerWidth <= 560);
  window.addEventListener('resize', onResize);
  return () => window.removeEventListener('resize', onResize);
}, []);

useEffect(() => {
  if (!isEditingSessionDetail || !sessionNotesTextareaRef.current) return;
  const el = sessionNotesTextareaRef.current;
  el.style.height = 'auto';
  el.style.height = `${Math.max(44, el.scrollHeight)}px`;
}, [isEditingSessionDetail, sessionDraft.notes]);

const startSession = async (modeOverride = null) => {
  if (!user) return;
  const loc = locationInput.trim() || 'Unnamed';
  const startedAt = new Date().toISOString();
  const createdAt = startedAt;
  const updatedAt = startedAt;
  const lastViewedAt = startedAt;
  const resolvedMode = modeOverride || (currentMode === 'all' ? 'tunes' : currentMode);
  const sessionData = {
    location: loc,
    startedAt,
    createdAt,
    updatedAt,
    lastViewedAt,
    mode: resolvedMode,
    segments: [{ startedAt, endedAt: null }],
  };

  try {
    const docId = crypto.randomUUID();
    let sessionDoc;

    if (navigator.onLine) {
      await setDoc(doc(db, 'users', user.uid, 'sessions', docId), {
        ...sessionData,
        id: docId,
        startedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastViewedAt: serverTimestamp(),
      });
      sessionDoc = { id: docId };
    } else {
      await dexie.sessions.put({ id: docId, ...sessionData });
      await addPendingWrite({
        type: 'add',
        collection: 'sessions',
        docId,
        data: { id: docId, ...sessionData }
      });
      sessionDoc = { id: docId };
    }

    setActiveSession({ id: sessionDoc.id, ...sessionData });
    localStorage.setItem(ACTIVE_SESSION_STORAGE_KEY, JSON.stringify({ id: sessionDoc.id, ...sessionData }));
    setLocationInput('');
  } catch (err) {
    console.error('Error starting session:', err);
    showNotice('Could not start session.', 'error');
  }
};

const endSession = async () => {
  if (!user || !activeSession) return;

  try {
    const endedAt = new Date().toISOString();
    const updatedAt = endedAt;
    const existingSession = sessions.find((s) => s.id === activeSession.id);
    const baseSegments = normalizeSessionSegments(existingSession || activeSession);
    const nextSegments = [...baseSegments];
    if (nextSegments.length === 0) {
      nextSegments.push({ startedAt: toSessionDate(activeSession.startedAt) || new Date(), endedAt: toSessionDate(endedAt) });
    } else {
      const lastIndex = nextSegments.length - 1;
      if (!nextSegments[lastIndex].endedAt) {
        nextSegments[lastIndex] = { ...nextSegments[lastIndex], endedAt: toSessionDate(endedAt) };
      } else {
        nextSegments.push({ startedAt: toSessionDate(activeSession.startedAt) || new Date(), endedAt: toSessionDate(endedAt) });
      }
    }
    const updatedData = {
      endedAt,
      updatedAt,
      segments: nextSegments.map((segment) => ({
        startedAt: segment.startedAt?.toISOString?.() || segment.startedAt,
        endedAt: segment.endedAt?.toISOString?.() || segment.endedAt || null,
      })),
    };
    const sessionRef = doc(db, 'users', user.uid, 'sessions', activeSession.id);

    if (navigator.onLine) {
      await updateDoc(sessionRef, {
        endedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        segments: updatedData.segments,
      });
    } else {
      await dexie.sessions.update(activeSession.id, updatedData);
      await addPendingWrite({
        type: 'update',
        collection: 'sessions',
        docId: activeSession.id,
        data: { id: activeSession.id, ...updatedData }
      });
    }

    setSessions((prev) => prev.map((session) => (
      session.id === activeSession.id
        ? { ...session, endedAt: toSessionDate(endedAt), updatedAt: toSessionDate(updatedAt), segments: normalizeSessionSegments(updatedData) }
        : session
    )));
    setSelectedSession((prev) => (
      prev?.id === activeSession.id
        ? { ...prev, endedAt: toSessionDate(endedAt), updatedAt: toSessionDate(updatedAt), segments: normalizeSessionSegments(updatedData) }
        : prev
    ));
    setActiveSession(null);
    localStorage.removeItem(ACTIVE_SESSION_STORAGE_KEY);
  } catch (err) {
    console.error('Failed to log session end:', err);
    showNotice('Could not end session.', 'error');
  }
};

useEffect(() => {
  if (!user) return undefined;

  let sessionsCache = [];
  let tunesCache = [];
  let setsCache = [];
  let unsubscribeSessions = null;
  let unsubscribeTunes = null;
  let unsubscribeSets = null;
  let cancelled = false;

  const applyHydratedData = () => {
    if (cancelled) return;
    setTunes(tunesCache);
    setSets(setsCache);
    setSessions(hydrateSessions(sessionsCache, tunesCache, setsCache));
  };

  const cleanupListeners = () => {
    if (unsubscribeSessions) {
      unsubscribeSessions();
      unsubscribeSessions = null;
    }
    if (unsubscribeTunes) {
      unsubscribeTunes();
      unsubscribeTunes = null;
    }
    if (unsubscribeSets) {
      unsubscribeSets();
      unsubscribeSets = null;
    }
  };

  const loadOffline = async () => {
    try {
      const [offlineTunes, offlineSets, offlineSessions] = await Promise.all([
        dexie.tunes.toArray(),
        dexie.sets.toArray(),
        dexie.sessions.toArray()
      ]);
      tunesCache = offlineTunes;
      setsCache = offlineSets;
      sessionsCache = offlineSessions;
      applyHydratedData();
    } catch (err) {
      console.warn('Failed loading offline session data:', err);
    }
  };

  const setupOnline = async () => {
    try {
      await flushPendingWrites(user);
    } catch (err) {
      console.warn('Pending write flush failed:', err);
    }

    const sessionsRef = collection(db, 'users', user.uid, 'sessions');
    const tunesRef = collection(db, 'users', user.uid, 'tunes');
    const setsRef = collection(db, 'users', user.uid, 'sets');

    unsubscribeSessions = onSnapshot(sessionsRef, (sessionSnap) => {
      sessionsCache = sessionSnap.docs.map((snap) => ({ id: snap.id, ...snap.data() }));
      dexie.sessions.bulkPut(sessionsCache).catch(() => {});
      applyHydratedData();
    });

    unsubscribeTunes = onSnapshot(tunesRef, (tuneSnap) => {
      tunesCache = tuneSnap.docs.map((snap) => ({ id: snap.id, ...snap.data() }));
      dexie.tunes.bulkPut(tunesCache).catch(() => {});
      applyHydratedData();
    });

    unsubscribeSets = onSnapshot(setsRef, (setSnap) => {
      setsCache = setSnap.docs.map((snap) => ({ id: snap.id, ...snap.data() }));
      dexie.sets.bulkPut(setsCache).catch(() => {});
      applyHydratedData();
    });
  };

  const refreshByConnection = () => {
    cleanupListeners();
    if (navigator.onLine) {
      setupOnline();
    } else {
      loadOffline();
    }
  };

  refreshByConnection();
  window.addEventListener('online', refreshByConnection);
  window.addEventListener('offline', refreshByConnection);

  return () => {
    cancelled = true;
    window.removeEventListener('online', refreshByConnection);
    window.removeEventListener('offline', refreshByConnection);
    cleanupListeners();
  };
}, [user]);



  const toggleSessionSelection = (key) => {
    setSelectedSessionKeys(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const toggleSelectAll = () => {
    if (allVisibleSessionsSelected) {
      setSelectedSessionKeys((prev) => prev.filter((id) => !visibleSessionKeys.includes(id)));
    } else {
      setSelectedSessionKeys((prev) => Array.from(new Set([...prev, ...visibleSessionKeys])));
    }
  };

const deleteSelectedSessions = async () => {
  if (!user || selectedSessionKeys.length === 0) return;

  const containsActive = selectedSessionKeys.includes(activeSession?.id);
  if (containsActive) {
    showNotice('End the active session before deleting it.', 'error');
    return;
  }

  // Show undo banner immediately
setPendingDelete({
  tuneBackups: [],
  setBackups: [],
  sessionKeys: selectedSessionKeys,
  sessionBackups: sessions.filter(s => selectedSessionKeys.includes(s.id))
});

  setSelectedSessionKeys([]);
  setSessions(s => [...s]);

  const timer = setTimeout(() => {
    setPendingDelete(null);
    setUndoTimer(null);
  }, 5000);
  setUndoTimer(timer);

  const affectedTuneSnapshots = [];
  const affectedSetSnapshots = [];

  // 🔥 Delete each session
  for (const sessionId of selectedSessionKeys) {
    if (navigator.onLine) {
      await deleteDoc(doc(db, 'users', user.uid, 'sessions', sessionId));
    } else {
      await dexie.sessions.delete(sessionId);
      await dexie.pendingWrites.put({
        id: `${sessionId}__delete`,
        type: 'delete',
        collection: 'sessions',
        docId: sessionId,
        data: {}
      });
    }
  }

  // 🎯 Clean up all affected tunes
  for (const tune of tunes) {
    const ref = doc(db, 'users', user.uid, 'tunes', tune.id);

    let original;
    if (navigator.onLine) {
      const snap = await getDoc(ref);
      if (!snap.exists()) continue;
      original = snap.data();
    } else {
      original = await dexie.tunes.get(tune.id);
      if (!original) continue;
    }

    const updatedSessions = (original.sessions || []).filter(s => !selectedSessionKeys.includes(s.sessionId));

    if (updatedSessions.length !== (original.sessions || []).length) {
      affectedTuneSnapshots.push({ id: tune.id, data: original });

      await dexie.tunes.update(tune.id, { sessions: updatedSessions });

      await dexie.pendingWrites.put({
        id: `${tune.id}__sessionRemove`,
        type: 'update',
        collection: 'tunes',
        docId: tune.id,
        data: { sessions: updatedSessions }
      });

      if (navigator.onLine) {
        await updateDoc(ref, { sessions: updatedSessions });
      }
    }
  }

  // 🎯 Clean up all affected sets
  for (const set of sets) {
    const ref = doc(db, 'users', user.uid, 'sets', set.id);

    let original;
    if (navigator.onLine) {
      const snap = await getDoc(ref);
      if (!snap.exists()) continue;
      original = snap.data();
    } else {
      original = await dexie.sets.get(set.id);
      if (!original) continue;
    }

    const updatedSessions = (original.sessions || []).filter(s => !selectedSessionKeys.includes(s.sessionId));

    if (updatedSessions.length !== (original.sessions || []).length) {
      affectedSetSnapshots.push({ id: set.id, data: original });

      await dexie.sets.update(set.id, { sessions: updatedSessions });

      await dexie.pendingWrites.put({
        id: `${set.id}__sessionRemove`,
        type: 'update',
        collection: 'sets',
        docId: set.id,
        data: { sessions: updatedSessions }
      });

      if (navigator.onLine) {
        await updateDoc(ref, { sessions: updatedSessions });
      }
    }
  }

  // Fill in tune backups after processing
setPendingDelete(prev => {
  if (!prev) return null;
  return {
    ...prev,
    tuneBackups: affectedTuneSnapshots,
    setBackups: affectedSetSnapshots
  };
});

};


const reopenSession = async () => {
  if (!selectedSession || !user) return;

  try {
    const sessionRef = doc(db, 'users', user.uid, 'sessions', selectedSession.id);
    const resumedAt = new Date().toISOString();
    const updatedAt = resumedAt;
    const baseSegments = normalizeSessionSegments(selectedSession);
    const nextSegments = [...baseSegments];
    if (nextSegments.length === 0) {
      nextSegments.push({ startedAt: toSessionDate(selectedSession.startedAt) || new Date(), endedAt: null });
    } else {
      const lastIndex = nextSegments.length - 1;
      if (nextSegments[lastIndex].endedAt) {
        nextSegments.push({ startedAt: toSessionDate(resumedAt), endedAt: null });
      }
    }
    const serializedSegments = nextSegments.map((segment) => ({
      startedAt: segment.startedAt?.toISOString?.() || segment.startedAt,
      endedAt: segment.endedAt?.toISOString?.() || segment.endedAt || null,
    }));

    if (navigator.onLine) {
      await updateDoc(sessionRef, { endedAt: null, updatedAt: serverTimestamp(), segments: serializedSegments });
    } else {
      await dexie.sessions.update(selectedSession.id, { endedAt: null, updatedAt, segments: serializedSegments });
      await addPendingWrite({
        type: 'update',
        collection: 'sessions',
        docId: selectedSession.id,
        data: { id: selectedSession.id, endedAt: null, updatedAt, segments: serializedSegments }
      });
    }

    const activePayload = {
      id: selectedSession.id,
      location: selectedSession.location || 'Unnamed',
      startedAt: resumedAt,
      mode: selectedSession.mode || 'tunes'
    };

    setActiveSession(activePayload);
    localStorage.setItem(ACTIVE_SESSION_STORAGE_KEY, JSON.stringify(activePayload));
    setSessions((prev) => prev.map((session) => (
      session.id === selectedSession.id
        ? { ...session, endedAt: null, updatedAt: toSessionDate(updatedAt), segments: normalizeSessionSegments({ segments: serializedSegments, startedAt: session.startedAt, endedAt: null }) }
        : session
    )));
    setSelectedSession(prev => (prev ? { ...prev, endedAt: null, updatedAt: toSessionDate(updatedAt), segments: normalizeSessionSegments({ segments: serializedSegments, startedAt: prev.startedAt, endedAt: null }) } : prev));
    showNotice('Session reopened and set as active.');
  } catch (err) {
    console.error('Failed to reopen session:', err);
    showNotice('Could not reopen session.', 'error');
  }
};

const moveSessionDraftItem = (index, direction) => {
  setSessionDraft((prev) => {
    const next = [...(prev.items || [])];
    const target = index + direction;
    if (target < 0 || target >= next.length) return prev;
    const temp = next[index];
    next[index] = next[target];
    next[target] = temp;
    const updated = { ...prev, items: next };
    saveSessionDetailEdits(updated);
    return updated;
  });
};

const removeSessionDraftItem = (item, index) => {
  setSessionDraft((prev) => {
    const updated = {
      ...prev,
      items: (prev.items || []).filter((it, i) => !(i === index && String(it.id) === String(item.id) && (it._entryType || 'tune') === (item._entryType || 'tune')))
    };
    saveSessionDetailEdits(updated);
    return updated;
  });
};

const addSessionDraftItem = (item) => {
  setSessionDraft((prev) => {
    const entryType = item.itemType === 'set' ? 'set' : 'tune';
    const exists = (prev.items || []).some((it) => String(it.id) === String(item.id) && (it._entryType || 'tune') === entryType);
    if (exists) return prev;
    const updated = {
      ...prev,
      items: [...(prev.items || []), { ...item, _entryType: entryType }]
    };
    saveSessionDetailEdits(updated);
    return updated;
  });
};
const handleSessionDraftDragStart = (index) => {
  setSessionDragIndex(index);
};
const handleSessionDraftDrop = (targetIndex) => {
  if (sessionDragIndex === null || targetIndex === sessionDragIndex) {
    setSessionDragIndex(null);
    return;
  }
  setSessionDraft((prev) => {
    const next = [...(prev.items || [])];
    const [moved] = next.splice(sessionDragIndex, 1);
    next.splice(targetIndex, 0, moved);
    const updated = { ...prev, items: next };
    saveSessionDetailEdits(updated);
    return updated;
  });
  setSessionDragIndex(null);
};

const saveSessionDetailEdits = async (draftOverride = null) => {
  if (!user || !selectedSession) return;
  const draft = draftOverride || sessionDraft;
  const updated = {
    location: (draft.location || '').trim() || 'Unnamed',
    notes: (draft.notes || '').trim(),
    startedAt: draft.startedAt ? new Date(draft.startedAt).toISOString() : null,
    endedAt: draft.endedAt ? new Date(draft.endedAt).toISOString() : null,
    tunes: (draft.items || []).map((item) => ({
      id: item.id,
      title: item.title || '',
      type: item._entryType === 'set' ? 'set' : (item.type || ''),
      key: item.key || ''
    })),
    customTags: normalizeTagsArray(draft.customTags || []),
    updatedAt: new Date().toISOString()
  };

  const sessionId = selectedSession.id;
  const sessionRef = doc(db, 'users', user.uid, 'sessions', sessionId);
  const prefsRef = doc(db, 'users', user.uid, 'preferences', 'settings');
  const previousItems = selectedSession.tunes || [];
  const nextItems = draft.items || [];
  const previousKeys = new Set(previousItems.map((item) => `${item._entryType || 'tune'}:${item.id}`));
  const nextKeys = new Set(nextItems.map((item) => `${item._entryType || 'tune'}:${item.id}`));
  const removedItems = previousItems.filter((item) => !nextKeys.has(`${item._entryType || 'tune'}:${item.id}`));
  const addedItems = nextItems.filter((item) => !previousKeys.has(`${item._entryType || 'tune'}:${item.id}`));

  try {
    const snap = await getDoc(prefsRef);
    const oldTags = snap.exists() ? snap.data().customTags || [] : [];
    const mergedTags = Array.from(new Set([
      ...normalizeTagsArray(draft.customTags || []),
      ...oldTags.map(normalizeTag)
    ]));

    if (navigator.onLine) {
      await updateDoc(prefsRef, { customTags: mergedTags });
      await updateDoc(sessionRef, updated);
    } else {
      await addPendingWrite({
        id: 'settings',
        type: 'update',
        collection: 'preferences',
        docId: 'settings',
        data: { customTags: mergedTags }
      });
      await addPendingWrite({
        id: sessionId,
        type: 'update',
        collection: 'sessions',
        docId: sessionId,
        data: { id: sessionId, ...updated }
      });
    }

    await dexie.sessions.put({ id: sessionId, ...(selectedSession || {}), ...updated });

    for (const item of addedItems) {
      const collectionName = item._entryType === 'set' ? 'sets' : 'tunes';
      const ref = doc(db, 'users', user.uid, collectionName, item.id);
      const original = collectionName === 'sets'
        ? await dexie.sets.get(item.id)
        : await dexie.tunes.get(item.id);
      const existingSessions = Array.isArray(original?.sessions) ? original.sessions : [];
      if (existingSessions.some((s) => s.sessionId === sessionId)) continue;
      const updatedSessions = [
        ...existingSessions,
        {
          sessionId,
          location: updated.location,
          startedAt: updated.startedAt || new Date().toISOString()
        }
      ];
      if (navigator.onLine) await updateDoc(ref, { sessions: updatedSessions });
      if (collectionName === 'sets') await dexie.sets.update(item.id, { sessions: updatedSessions });
      else await dexie.tunes.update(item.id, { sessions: updatedSessions });
      await addPendingWrite({
        id: `${item.id}__sessionAdd`,
        type: 'update',
        collection: collectionName,
        docId: item.id,
        data: { sessions: updatedSessions }
      });
    }

    for (const item of removedItems) {
      const collectionName = item._entryType === 'set' ? 'sets' : 'tunes';
      const ref = doc(db, 'users', user.uid, collectionName, item.id);
      const original = collectionName === 'sets'
        ? await dexie.sets.get(item.id)
        : await dexie.tunes.get(item.id);
      if (!original) continue;
      const updatedSessions = (original.sessions || []).filter((s) => s.sessionId !== sessionId);
      if (navigator.onLine) await updateDoc(ref, { sessions: updatedSessions });
      if (collectionName === 'sets') await dexie.sets.update(item.id, { sessions: updatedSessions });
      else await dexie.tunes.update(item.id, { sessions: updatedSessions });
      await addPendingWrite({
        id: `${item.id}__sessionRemove`,
        type: 'update',
        collection: collectionName,
        docId: item.id,
        data: { sessions: updatedSessions }
      });
    }

    const mergedSelected = { ...selectedSession, ...updated, startedAt: updated.startedAt ? new Date(updated.startedAt) : selectedSession.startedAt, endedAt: updated.endedAt ? new Date(updated.endedAt) : null, tunes: nextItems };
    setSelectedSession(mergedSelected);
    setSessions((prev) => prev.map((s) => (s.id === sessionId ? { ...s, ...updated } : s)));
  } catch (err) {
    console.error('Error saving session edits:', err);
    showNotice('Failed to save session updates.', 'error');
  }
};
const persistSessionMetaUpdates = async (partial) => {
  if (!user || !selectedSession) return;
  const updates = { ...(partial || {}), updatedAt: new Date().toISOString() };
  try {
    const sessionRef = doc(db, 'users', user.uid, 'sessions', selectedSession.id);
    if (navigator.onLine) {
      await updateDoc(sessionRef, updates);
    } else {
      await addPendingWrite({
        id: `${selectedSession.id}__update__${Date.now()}`,
        type: 'update',
        collection: 'sessions',
        docId: selectedSession.id,
        data: { id: selectedSession.id, ...updates }
      });
    }
    await dexie.sessions.put({ id: selectedSession.id, ...(selectedSession || {}), ...updates });
    setSelectedSession((prev) => (prev ? { ...prev, ...updates } : prev));
    setSessions((prev) => prev.map((s) => (s.id === selectedSession.id ? { ...s, ...updates } : s)));
  } catch (err) {
    console.error('Failed to update session metadata:', err);
    showNotice('Failed to update session.', 'error');
  }
};

const undoDelete = async () => {
  if (!user || !pendingDelete) return;

  if (undoTimer) {
    clearTimeout(undoTimer);
    setUndoTimer(null);
  }

  // Restore deleted sessions
  for (const backup of pendingDelete.sessionBackups || []) {
    const id = backup.id;
    const sessionRef = doc(db, 'users', user.uid, 'sessions', id);

    if (navigator.onLine) {
      await setDoc(sessionRef, backup);
    }

    await dexie.sessions.put({ ...backup, id });

    await dexie.pendingWrites.put({
      id: `${id}__undo`,
      type: 'update',
      collection: 'sessions',
      docId: id,
      data: backup
    });
  }

  // Restore affected tunes
  for (const { id, data } of pendingDelete.tuneBackups) {
    const ref = doc(db, 'users', user.uid, 'tunes', id);

    if (navigator.onLine) {
      await setDoc(ref, data);
    }

    await dexie.tunes.put({ ...data, id });

    await dexie.pendingWrites.put({
      id: `${id}__undo`,
      type: 'update',
      collection: 'tunes',
      docId: id,
      data
    });
  }

  // Restore affected sets
  for (const { id, data } of (pendingDelete.setBackups || [])) {
    const ref = doc(db, 'users', user.uid, 'sets', id);

    if (navigator.onLine) {
      await setDoc(ref, data);
    }

    await dexie.sets.put({ ...data, id });

    await dexie.pendingWrites.put({
      id: `${id}__undo`,
      type: 'update',
      collection: 'sets',
      docId: id,
      data
    });
  }

  // ✅ Force visual refresh
setSessions(prev => {
  const restored = pendingDelete.sessionBackups.filter(
    restored => !prev.some(s => s.id === restored.id)
  );
  return [...prev, ...restored];
});

  setPendingDelete(null);
  setUndoTimer(null);
};

const swipeDeleteSession = async (s) => {
  if (!user) return;
  if (activeSession?.id === s.id) {
    showNotice('End the active session before deleting it.', 'error');
    return;
  }

  setPendingDelete({
    tuneBackups: [],
    setBackups: [],
    sessionKeys: [s.id],
    sessionBackups: [s],
  });

  if (undoTimer) clearTimeout(undoTimer);
  const timer = setTimeout(() => {
    setPendingDelete(null);
    setUndoTimer(null);
  }, 5000);
  setUndoTimer(timer);

  const affectedTuneSnapshots = [];
  const affectedSetSnapshots = [];

  if (navigator.onLine) {
    await deleteDoc(doc(db, 'users', user.uid, 'sessions', s.id));
  } else {
    await dexie.sessions.delete(s.id);
    await dexie.pendingWrites.put({
      id: `${s.id}__delete`,
      type: 'delete',
      collection: 'sessions',
      docId: s.id,
      data: {}
    });
  }

  for (const tune of tunes) {
    const ref = doc(db, 'users', user.uid, 'tunes', tune.id);
    let original;
    if (navigator.onLine) {
      const snap = await getDoc(ref);
      if (!snap.exists()) continue;
      original = snap.data();
    } else {
      original = await dexie.tunes.get(tune.id);
      if (!original) continue;
    }
    const updatedSessions = (original.sessions || []).filter(ss => ss.sessionId !== s.id);
    if (updatedSessions.length !== (original.sessions || []).length) {
      affectedTuneSnapshots.push({ id: tune.id, data: original });
      await dexie.tunes.update(tune.id, { sessions: updatedSessions });
      await dexie.pendingWrites.put({ id: `${tune.id}__sessionRemove`, type: 'update', collection: 'tunes', docId: tune.id, data: { sessions: updatedSessions } });
      if (navigator.onLine) await updateDoc(ref, { sessions: updatedSessions });
    }
  }

  for (const set of sets) {
    const ref = doc(db, 'users', user.uid, 'sets', set.id);
    let original;
    if (navigator.onLine) {
      const snap = await getDoc(ref);
      if (!snap.exists()) continue;
      original = snap.data();
    } else {
      original = await dexie.sets.get(set.id);
      if (!original) continue;
    }
    const updatedSessions = (original.sessions || []).filter(ss => ss.sessionId !== s.id);
    if (updatedSessions.length !== (original.sessions || []).length) {
      affectedSetSnapshots.push({ id: set.id, data: original });
      await dexie.sets.update(set.id, { sessions: updatedSessions });
      await dexie.pendingWrites.put({ id: `${set.id}__sessionRemove`, type: 'update', collection: 'sets', docId: set.id, data: { sessions: updatedSessions } });
      if (navigator.onLine) await updateDoc(ref, { sessions: updatedSessions });
    }
  }

  setPendingDelete(prev => prev ? { ...prev, tuneBackups: affectedTuneSnapshots, setBackups: affectedSetSnapshots } : null);
};

const swipePinSession = async (s) => {
  if (!user) return;
  const newPinned = !s.pinned;
  try {
    if (navigator.onLine) {
      await updateDoc(doc(db, 'users', user.uid, 'sessions', s.id), { pinned: newPinned });
    } else {
      await dexie.sessions.update(s.id, { pinned: newPinned });
      await addPendingWrite({ type: 'update', collection: 'sessions', docId: s.id, data: { pinned: newPinned } });
    }
    setSessions(prev => prev.map(session => session.id === s.id ? { ...session, pinned: newPinned } : session));
  } catch (err) {
    console.error('Failed to pin session:', err);
  }
};

const handleBulkSendSessions = async (recipientUid, contact) => {
  if (!user) return;
  const myProfile = await getUserProfile(user.uid);
  const items = selectedSessionKeys.map(id => {
    const s = sessions.find(s => s.id === id);
    return { itemType: 'session', sessionId: id, sessionTitle: s?.location || new Date(s?.startedAt).toLocaleDateString() || 'Session' };
  });
  await sendBatchToUser(user.uid, myProfile, recipientUid, items);
};

const handleSwipeSendSession = async (recipientUid, _contact) => {
  if (!user) return;
  const myProfile = await getUserProfile(user.uid);
  const sessionTunes = (swipeSendTarget.tunes || []).map(t => ({
    id: t.id,
    title: t.title || '',
    type: t.type || 'tune',
  }));
  const sessionTitle = swipeSendTarget.location
    ? `Session at ${swipeSendTarget.location}`
    : swipeSendTarget.startedAt
      ? new Date(swipeSendTarget.startedAt).toLocaleDateString()
      : 'Session';
  await sendSessionToUser(user.uid, myProfile, recipientUid, swipeSendTarget.id, sessionTitle, sessionTunes);
};

const openSessionDetail = async (session) => {
  if (!session) return;
  setSelectedSession(session);
  if (!user || !session.id) return;

  const viewedAtIso = new Date().toISOString();
  const viewedAtDate = toSessionDate(viewedAtIso);
  const updates = { lastViewedAt: viewedAtIso };
  try {
    if (navigator.onLine) {
      await updateDoc(doc(db, 'users', user.uid, 'sessions', session.id), { lastViewedAt: serverTimestamp() });
    } else {
      await dexie.sessions.update(session.id, updates);
      await addPendingWrite({
        id: `${session.id}__lastViewedAt__${Date.now()}`,
        type: 'update',
        collection: 'sessions',
        docId: session.id,
        data: updates
      });
    }
    setSessions((prev) => prev.map((s) => (s.id === session.id ? { ...s, lastViewedAt: viewedAtDate } : s)));
    setSelectedSession((prev) => (prev?.id === session.id ? { ...prev, lastViewedAt: viewedAtDate } : prev));
  } catch (err) {
    console.warn('Failed to update session last viewed timestamp:', err);
  }
};

const getFilteredSessions = () => {
  const matches = [];

  for (const session of sessions) {
    if (currentMode !== 'all' && (session.mode || 'tunes') !== currentMode) continue;
    const query = filterQuery.toLowerCase();

    let matchingTuneTitle = null;

    const matchesSearch = !query || (
      (session.location?.toLowerCase().includes(query)) ||
      (session.notes?.toLowerCase().includes(query)) ||
      (Array.isArray(session.customTags) && session.customTags.some(tag => String(tag || '').toLowerCase().includes(query))) ||
      session.tunes?.some(t => {
        if (t.title?.toLowerCase().includes(query)) {
          matchingTuneTitle = t.title;
          return true;
        }
        return false;
      })
    );

const matchesGenre = !filterGenre || session.tunes?.some(t => t._entryType !== 'set' && normalizeGenre(t.genre) === filterGenre);

    const matchesTags = selectedTags.length === 0 || (
      tagLogic === 'AND'
        ? normalizeTagsArray(selectedTags).every(tag => normalizeTagsArray(session.customTags || []).includes(tag))
        : normalizeTagsArray(selectedTags).some(tag => normalizeTagsArray(session.customTags || []).includes(tag))
    );

    const matchesTime = matchesSessionTimeRange(
      session,
      filterTimeRange,
      filterTimeBasis,
      filterTimeMonth,
      filterTimeYear
    );

    if (matchesSearch && matchesGenre && matchesTags && matchesTime) {
      matches.push({ ...session, _matchedTuneTitle: matchingTuneTitle });
    }
  }

  return matches.sort((a, b) => {
    if (a.pinned && !b.pinned) return -1;
    if (!a.pinned && b.pinned) return 1;
    if (sortOrder === 'alpha') return (a.location || '').localeCompare(b.location || '');
    if (sortOrder === 'alphaDesc') return (b.location || '').localeCompare(a.location || '');
    const aTime = a.startedAt?.getTime?.() || 0;
    const bTime = b.startedAt?.getTime?.() || 0;
    return sortOrder === 'oldest' ? aTime - bTime : bTime - aTime;
  });
};


const filteredSessions = getFilteredSessions();
const visibleSessionKeys = filteredSessions.map((s) => s.id);
const allVisibleSessionsSelected = visibleSessionKeys.length > 0 && visibleSessionKeys.every((id) => selectedSessionKeys.includes(id));
const addSessionTagFromPreview = (tag) => {
  const normalized = normalizeTag(tag || '');
  if (!normalized) return;
  setShowTagFilterUI(true);
  setSelectedTags((prev) => (prev.includes(normalized) ? prev : [...prev, normalized]));
};
const availableSessionTags = React.useMemo(() => {
  const fromPrefs = Array.isArray(prefs?.customTags) ? prefs.customTags : [];
  const fromSessions = sessions.flatMap((session) => (Array.isArray(session.customTags) ? session.customTags : []));
  return normalizeTagsArray([...fromPrefs, ...fromSessions]).sort((a, b) => a.localeCompare(b));
}, [prefs?.customTags, sessions]);
const hasActiveTimeFilter =
  !!filterTimeRange ||
  filterTimeBasis !== 'updated' ||
  (filterTimeRange === 'month' && !!filterTimeMonth) ||
  (filterTimeRange === 'year' && !!filterTimeYear);
const hasActiveSessionFilters = Boolean(
  (filterQuery || '').trim() ||
  filterGenre ||
  selectedTags.length > 0 ||
  hasActiveTimeFilter
);
const sessionGenreValues = [...new Set([...(labels?.defaultGenres || []), ...tunes.filter(t => currentMode === 'all' || (t.mode || 'tunes') === currentMode).map(t => normalizeGenre(t.genre))])].filter(Boolean).sort();
const modernGenreOptions = [
  { value: '', label: `All ${labels?.genreLabelPlural || 'Genres'}` },
  ...sessionGenreValues.map((g) => ({ value: g, label: g }))
];
const modernSortOptions = [
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
  { value: 'alpha', label: 'A-Z' },
  { value: 'alphaDesc', label: 'Z-A' }
];
const modernBulkActionOptions = [
  {
    label: 'Bulk Actions',
    options: [
      { value: 'delete', label: '🗑 Delete selected' },
      { value: 'addtags', label: '🏷 Add tags' },
      { value: 'action:send', label: '✉ Send to Contact' },
      { value: 'action:export', label: '📥 Export Selected' },
    ],
  },
];
const formatTimeMonthLabel = (monthValue) => {
  if (!monthValue) return 'Selected month';
  const [y, m] = String(monthValue).split('-').map(Number);
  if (!y || !m) return 'Selected month';
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
};
const timeSummaryLabel =
  !filterTimeRange && filterTimeBasis === 'updated'
    ? 'Time'
    : `Time: ${
      filterTimeRange === 'past-week' ? 'Past Week'
        : filterTimeRange === 'this-month' ? 'This Month'
          : filterTimeRange === 'past-month' ? 'Past Month'
            : filterTimeRange === 'past-year' ? 'Past Year'
              : filterTimeRange === 'year' ? (filterTimeYear || 'Selected year')
                : filterTimeRange === 'month' ? formatTimeMonthLabel(filterTimeMonth)
                  : 'No Time Filter'
    } (${filterTimeBasis === 'created' ? 'Created' : filterTimeBasis === 'viewed' ? 'Viewed' : 'Updated'})`;
const timeRangeOptions = [
  { value: 'past-week', label: 'Past week' },
  { value: 'this-month', label: 'This month' },
  { value: 'past-month', label: 'Past month' },
  { value: 'past-year', label: 'Past year' },
  { value: 'year', label: 'Pick year' },
  { value: 'month', label: 'Pick month' }
];
const activeUiSize =
  (typeof document !== 'undefined' && document.body?.dataset?.uiSize) ||
  (typeof window !== 'undefined' ? localStorage.getItem('uiSize') : null) ||
  'medium';
const timeBasisOptions = [
  { value: 'updated', label: 'Updated' },
  { value: 'viewed', label: 'Viewed' },
  { value: 'created', label: 'Created' }
];
const handleSessionTimeRangeSelect = (value) => {
  setFilterTimeRange(value);
  if (value === 'month') {
    const [y] = (filterTimeMonth || '').split('-').map(Number);
    setMonthPickerYear(y || new Date().getFullYear());
    setFilterTimeYear('');
  } else if (value === 'year') {
    setMonthPickerYear(Number(filterTimeYear) || new Date().getFullYear());
    setFilterTimeMonth('');
  } else {
    setFilterTimeMonth('');
    setFilterTimeYear('');
  }
};

const sessionModeLabels = getModeLabelsForEntity(currentMode, selectedSession?.mode || currentMode);
const sessionCollectionLabel = sessionModeLabels?.Collection || 'Set';
const sessionCollectionsLabel = sessionModeLabels?.Collections || 'Sets';
const isNotesSessionMode = (selectedSession?.mode || currentMode) === 'notes';
const sessionItemKeyGroups = {};
tunes.forEach((t) => {
  const nk = normalizeKey(t.key);
  if (!nk) return;
  const root = keyRoot(nk);
  if (!sessionItemKeyGroups[root]) sessionItemKeyGroups[root] = new Set();
  sessionItemKeyGroups[root].add(nk);
});
const notesModeSessionCandidates = tunes.filter((t) => ((t?.mode || 'tunes') === 'notes') || normalizeType(t?.type) === 'note');
const sessionGenreOptions = [...new Set((isNotesSessionMode ? notesModeSessionCandidates : tunes).map((t) => (t.genre || '').trim().toLowerCase()).filter(Boolean))].sort();
const sessionTypeOptions = [...new Set((isNotesSessionMode ? notesModeSessionCandidates : tunes).map((t) => normalizeType(t.type)).filter(Boolean))].sort();
const sessionTypeFilterOptions = isNotesSessionMode
  ? [
    { value: 'all', label: `Notes & ${sessionCollectionsLabel}` },
    { value: 'note', label: 'Notes only' },
    { value: 'set', label: `${sessionCollectionsLabel} only` },
  ]
  : [
    { value: 'all', label: 'Tunes & Sets' },
    { value: 'tune', label: 'Tunes only' },
    { value: 'set', label: `${sessionCollectionsLabel} only` },
  ];
const isNoteCandidate = (item) => ((item?.mode || 'tunes') === 'notes') || normalizeType(item?.type) === 'note';
const isTuneCandidate = (item) => !isNoteCandidate(item);
const combinedSessionItems = [
  ...tunes
    .filter((t) => (isNotesSessionMode ? isNoteCandidate(t) : isTuneCandidate(t)))
    .map((t) => ({ ...t, itemType: isNotesSessionMode ? 'note' : 'tune' })),
  ...sets
    .filter((s) => !s.isProgram)
    .filter((s) => (isNotesSessionMode ? (s.mode || 'tunes') === 'notes' : (s.mode || 'tunes') !== 'notes'))
    .map((s) => ({ ...s, itemType: 'set' }))
];
const filteredSessionItemsForEditor = combinedSessionItems.filter((item) => {
  if (sessionItemTypeFilter !== 'all' && item.itemType !== sessionItemTypeFilter) return false;
  if (isNotesSessionMode && item.itemType === 'tune') return false;
  const nk = normalizeKey(item.key || '');
  const matchAny = !sessionItemSearchAny || Object.values(item).some((val) =>
    typeof val === 'string' && val.toLowerCase().includes(sessionItemSearchAny.toLowerCase())
  );
  const matchType = isNotesSessionMode || item.itemType === 'set' || !sessionItemFilterType || normalizeType(item.type) === normalizeType(sessionItemFilterType);
  const matchKey = isNotesSessionMode || item.itemType === 'set' || !sessionItemFilterKey
    || nk === sessionItemFilterKey
    || (sessionItemFilterKey.startsWith('root:') && keyRoot(nk) === sessionItemFilterKey.replace('root:', ''));
  const matchGenre = isNotesSessionMode || item.itemType === 'set' || !sessionItemFilterGenre || ((item.genre || '').trim().toLowerCase() === sessionItemFilterGenre);
  return matchAny && matchType && matchKey && matchGenre;
});



  if (selectedSession) {
    const selectedSessionIndex = filteredSessions.findIndex((s) => s.id === selectedSession.id);
    const canPrevSession = selectedSessionIndex > 0;
    const canNextSession = selectedSessionIndex !== -1 && selectedSessionIndex < filteredSessions.length - 1;
    const compactActionStyle = isCompactActions
      ? { fontSize: '0.8rem', padding: '5px 8px', lineHeight: 1.05 }
      : undefined;
    const goPrevSession = () => {
      if (!canPrevSession) return;
      openSessionDetail(filteredSessions[selectedSessionIndex - 1]);
    };
    const goNextSession = () => {
      if (!canNextSession) return;
      openSessionDetail(filteredSessions[selectedSessionIndex + 1]);
    };

    return (
      <PageWrapperAuto>
<div className="top-detail-actions-bar detail-actions-bar">
  <div className="top-detail-actions-side top-detail-actions-left detail-actions-side detail-actions-left">
    <button
      className="btn-subtle search-control-btn top-detail-action-btn detail-action-btn"
      onClick={goPrevSession}
      disabled={!canPrevSession}
      style={{ ...(compactActionStyle || {}), ...(!canPrevSession ? { opacity: 0.45, cursor: 'not-allowed' } : {}) }}
      title="Previous"
    >
      ← Prev
    </button>
    <button
      className="btn-subtle search-control-btn top-detail-action-btn detail-action-btn"
      onClick={() => {
        setSelectedSession(null); // just leave activeSession alone
      }}
      style={compactActionStyle}
      title="Back to Sessions"
    >
      ← Sessions
    </button>
  </div>
  <div className="top-detail-actions-side top-detail-actions-right detail-actions-side detail-actions-right">
    <button
      className="btn-subtle search-control-btn top-detail-action-btn detail-action-btn"
      onClick={() => setSwipeSendTarget(selectedSession)}
      title="Send / Export"
    >
      📤 Send / Export
    </button>
    <button
      className="btn-subtle search-control-btn top-detail-action-btn detail-action-btn"
      onClick={goNextSession}
      disabled={!canNextSession}
      style={{ ...(compactActionStyle || {}), ...(!canNextSession ? { opacity: 0.45, cursor: 'not-allowed' } : {}) }}
      title="Next"
    >
      Next →
    </button>
  </div>
</div>
{notice && (
  <div style={{
    margin: '0.5rem 0 1rem',
    padding: '8px 12px',
    borderRadius: '8px',
    textAlign: 'center',
    backgroundColor: notice.tone === 'error' ? '#5a1f1f' : '#1f3f5a',
    color: '#fff',
    fontSize: '0.9rem'
  }}>
    {notice.text}
  </div>
)}

<div style={{ textAlign: 'center', marginBottom: '0.2rem' }}>
  <PreviewModePill variant={(selectedSession.mode || 'tunes') === 'notes' ? 'note' : 'tune'}>
    {currentMode === 'all'
      ? getSessionModeTagLabel(selectedSession.mode || 'tunes')
      : 'Session'}
  </PreviewModePill>
</div>
<InlineEditableTitle
  isEditing={isEditingSessionTitle}
  value={editSessionTitleValue}
  displayValue={selectedSession.location || 'Unnamed Session'}
  onChange={setEditSessionTitleValue}
  onStartEditing={() => {
    setEditSessionTitleValue(selectedSession.location || '');
    setIsEditingSessionTitle(true);
  }}
  onCommit={async () => {
    const trimmed = (editSessionTitleValue || '').trim();
    await persistSessionMetaUpdates({ location: trimmed || 'Unnamed' });
    setIsEditingSessionTitle(false);
  }}
  onCancel={() => {
    setEditSessionTitleValue(selectedSession.location || '');
    setIsEditingSessionTitle(false);
  }}
  rowClassName="inline-editable-title-row detail-title-row"
  displayClassName="inline-editable-title-display detail-title-display detail-title-shell detail-title-heading"
  inputClassName="search-input inline-editable-title-input detail-title-input detail-title-shell detail-title-heading"
  inputStyle={{ ...inputStyle, boxSizing: 'border-box' }}
/>
{activeSession?.id === selectedSession.id && (
  <p style={{ marginTop: '-2px', marginBottom: '6px', fontStyle: 'italic', color: 'green' }}>
    (currently active)
  </p>
)}
<DetailTagEditor
  tags={selectedSession.customTags || []}
  onChange={(tags) => persistSessionMetaUpdates({ customTags: tags })}
  suggestions={availableSessionTags}
  inputStyle={inputStyle}
/>
<div style={{ maxWidth: '760px', margin: '0 auto 0.35rem', display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '8px' }}>
  <div style={{ textAlign: 'center', border: '1px solid color-mix(in srgb, var(--card-border) 45%, transparent)', borderRadius: '8px', padding: '8px 6px' }}>
    <p style={{ margin: '0 0 4px', fontSize: '0.86rem', fontWeight: 700, opacity: 0.9 }}>Session Start</p>
    <p style={{ ...textStyle, margin: 0, fontSize: '0.86rem' }}>
      {formatDateTimeNoSeconds(selectedSession.startedAt || selectedSession.timestamp)}
    </p>
  </div>
  <div style={{ textAlign: 'center', border: '1px solid color-mix(in srgb, var(--card-border) 45%, transparent)', borderRadius: '8px', padding: '8px 6px' }}>
    <p style={{ margin: '0 0 4px', fontSize: '0.86rem', fontWeight: 700, opacity: 0.9 }}>Session End</p>
    <p style={{ ...textStyle, margin: 0, fontSize: '0.86rem' }}>
      {selectedSession.endedAt ? formatDateTimeNoSeconds(selectedSession.endedAt) : 'Active / Not ended'}
    </p>
  </div>
  <div style={{ textAlign: 'center', border: '1px solid color-mix(in srgb, var(--card-border) 45%, transparent)', borderRadius: '8px', padding: '8px 6px' }}>
    <p style={{ margin: '0 0 4px', fontSize: '0.86rem', fontWeight: 700, opacity: 0.9 }}>Total Duration</p>
    <p style={{ ...textStyle, margin: 0, fontSize: '0.86rem' }}>
      {formatDurationLabel(getSessionTotalOpenMinutes(selectedSession))}
    </p>
  </div>
</div>
<div style={{
  marginBottom: '20px',
  display: 'flex',
  justifyContent: 'center',
  gap: '1rem'
}}>
  {activeSession?.id !== selectedSession.id && (
    <button
      className="btn-subtle search-control-btn top-detail-action-btn detail-action-btn"
      style={compactActionStyle}
      onClick={reopenSession}
    >
      Reopen Session
    </button>
  )}
</div>

        <div style={{ marginBottom: "2rem" }}>
          <ul className="card-list">
            {(isEditingSessionDetail ? (sessionDraft.items || []) : selectedSession.tunes).map(tune => (
              <li
                key={tune.id}
                className="card"
                style={{
                  cursor: 'pointer',
                }}
                onClick={() => {
                  if (tune._entryType === 'set') {
                    navigate(`/sets/${tune.id}`);
                    return;
                  }
                  const sessionTunes = (isEditingSessionDetail ? (sessionDraft.items || []) : selectedSession.tunes)
                    .filter((t) => t._entryType !== 'set')
                    .map(t => ({ ...t, id: String(t.id) }));
                  const currentIndex = sessionTunes.findIndex(t => String(t.id) === String(tune.id));

                  navigate(`/tune/${tune.id}`, {
                    state: {
                      tune,
                      sessionTunes,
                      sessionTitle: selectedSession.location,
                      currentIndex
                    }
                  });
                }}


              >
<div className="preview-pill-inline-row">
  <div className="preview-pill-title-group">
    <strong>{tune.title}</strong>
    {tune._entryType === 'set' && (
      <PreviewModePill
        context="inline"
        variant={getCollectionBadge(tune.mode || 'tunes') === 'FOLDER' ? 'folder' : 'set'}
      >
        {getCollectionBadge(tune.mode || 'tunes')}
      </PreviewModePill>
    )}
  </div>
  <span className="preview-pill-suffix">
    {tune._entryType === 'set'
      ? `— ${(tune.tunes || []).length} item${(tune.tunes || []).length === 1 ? '' : 's'}`
      : `— ${tune.genre} / ${tune.type} / ${normalizeKey(tune.key)}${tune.status ? ` (${tune.status})` : ''}`}
  </span>
</div>
              </li>
            ))}
          </ul>
        </div>

        <div style={{ maxWidth: '860px', margin: '0 auto 0.7rem', textAlign: 'center' }}>
          <button
            className="btn-subtle search-control-btn detail-header-pill detail-header-pill-editing"
            onClick={() => {
              setIsEditingSessionDetail((prev) => {
                const next = !prev;
                setShowSessionOrganizer(next);
                return next;
              });
            }}
            aria-expanded={isEditingSessionDetail}
          >
            <span className="detail-header-pill-caret">{isEditingSessionDetail ? '▼' : '▶'}</span>
            {isEditingSessionDetail ? 'Done Editing Session' : 'Edit Session'}
          </button>

          {isEditingSessionDetail && (
            <div className="detail-pill-content" style={{ marginTop: '8px' }}>
              <div style={{ maxWidth: '760px', margin: '0 auto 0.9rem', display: 'grid', gap: '8px' }}>
                <textarea
                  ref={sessionNotesTextareaRef}
                  className="search-input add-entry-textarea add-notes-field"
                  rows={1}
                  style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', minHeight: '44px', overflow: 'hidden', resize: 'none', gridColumn: '1 / -1' }}
                  value={sessionDraft.notes || ''}
                  onChange={(e) => setSessionDraft((prev) => ({ ...prev, notes: e.target.value }))}
                  onBlur={() => saveSessionDetailEdits()}
                  placeholder="Notes"
                />
                <div className="organizer-row-two" style={{ gridColumn: '1 / -1', minWidth: 0 }}>
                  <div>
                    <p style={{ margin: '0 0 4px', fontSize: '0.86rem', fontWeight: 700, opacity: 0.9, textAlign: 'center' }}>Session Start</p>
                    <input
                      className="search-input"
                      style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', gridColumn: 'auto' }}
                      type="datetime-local"
                      value={sessionDraft.startedAt || ''}
                      onChange={(e) => setSessionDraft((prev) => ({ ...prev, startedAt: e.target.value }))}
                      onBlur={() => saveSessionDetailEdits()}
                    />
                  </div>
                  <div>
                    <p style={{ margin: '0 0 4px', fontSize: '0.86rem', fontWeight: 700, opacity: 0.9, textAlign: 'center' }}>Session End</p>
                    <input
                      className="search-input"
                      style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', gridColumn: 'auto' }}
                      type="datetime-local"
                      value={sessionDraft.endedAt || ''}
                      disabled={activeSession?.id === selectedSession.id}
                      onChange={(e) => setSessionDraft((prev) => ({ ...prev, endedAt: e.target.value }))}
                      onBlur={() => saveSessionDetailEdits()}
                    />
                  </div>
                </div>
              </div>

              <button
                className={`btn-subtle search-control-btn detail-organize-toggle-btn ${showSessionOrganizer ? 'search-control-active' : ''}`}
                onClick={() => setShowSessionOrganizer((v) => !v)}
                style={{ justifySelf: 'center' }}
              >
                {showSessionOrganizer ? '▲ Hide Organize/Add Items' : '▼ Organize/Add Items'}
              </button>
              <DetailOrganizeReorderPanel
                visible={showSessionOrganizer}
                items={sessionDraft.items || []}
                availableItems={filteredSessionItemsForEditor}
                inputStyle={inputStyle}
                actionBtnStyle={compactActionStyle}
                searchValue={sessionItemSearchAny}
                onSearchChange={setSessionItemSearchAny}
                searchPlaceholder={isNotesSessionMode ? 'Search notes' : 'Search any field'}
                searchRowControl={isNotesSessionMode ? (
                  <AppSelect
                    className="compact-action-select"
                    value={sessionItemTypeFilter}
                    onChange={setSessionItemTypeFilter}
                    options={sessionTypeFilterOptions}
                    placeholder={`Notes & ${sessionCollectionsLabel}`}
                    active={sessionItemTypeFilter !== 'all'}
                  />
                ) : null}
                onDragStart={handleSessionDraftDragStart}
                onDrop={handleSessionDraftDrop}
                onDragEnd={() => setSessionDragIndex(null)}
                onMoveUp={(index) => moveSessionDraftItem(index, -1)}
                onMoveDown={(index) => moveSessionDraftItem(index, 1)}
                onRemove={removeSessionDraftItem}
                renderItemLabel={(item) => (
                  <>
                    {item.title}
                    {isNotesSessionMode
                      ? ''
                      : ((item._entryType || item.type) === 'set'
                        ? ` (${sessionCollectionLabel})`
                        : ((item.tuneType || item.type || item.key) ? ` (${[item.tuneType || item.type, item.key].filter(Boolean).join(', ')})` : ''))}
                  </>
                )}
                renderAvailableItemLabel={(item) => (
                  <>{item.title}{(!isNotesSessionMode && item.itemType === 'set') ? ` (${sessionCollectionLabel})` : ''}</>
                )}
                getItemKey={(item, index) => `${item._entryType || item.type || 'tune'}-${item.id}-${index}`}
                getAvailableItemKey={(item) => `${item.itemType}-${item.id}`}
                isItemInSelection={(item) => {
                  const expectedEntryType = item.itemType === 'set' ? 'set' : 'tune';
                  return (sessionDraft.items || []).some((it) => String(it.id) === String(item.id) && (it._entryType || 'tune') === expectedEntryType);
                }}
                onAddItem={addSessionDraftItem}
                filterControls={(
                  <>
                    {!isNotesSessionMode && (
                      <AppSelect
                        className="compact-action-select"
                        value={sessionItemTypeFilter}
                        onChange={setSessionItemTypeFilter}
                        options={sessionTypeFilterOptions}
                        placeholder="Item scope"
                        active={sessionItemTypeFilter !== 'all'}
                      />
                    )}
                    <AppSelect
                      className="compact-action-select"
                      value={sessionItemFilterGenre}
                      onChange={setSessionItemFilterGenre}
                      options={[
                        { value: '', label: 'All categories' },
                        ...sessionGenreOptions.map((g) => ({ value: g, label: g.charAt(0).toUpperCase() + g.slice(1) }))
                      ]}
                      placeholder="All categories"
                      active={Boolean(sessionItemFilterGenre)}
                    />
                    <AppSelect
                      className="compact-action-select"
                      value={sessionItemFilterType}
                      onChange={setSessionItemFilterType}
                      options={[
                        { value: '', label: 'All types' },
                        ...sessionTypeOptions.map((type) => ({ value: type, label: type.charAt(0).toUpperCase() + type.slice(1) }))
                      ]}
                      placeholder="All types"
                      active={Boolean(sessionItemFilterType)}
                    />
                    {!isNotesSessionMode && (
                      <AppSelect
                        className="compact-action-select"
                        value={sessionItemFilterKey}
                        onChange={setSessionItemFilterKey}
                        options={[
                          { value: '', label: 'All keys' },
                          ...Object.keys(sessionItemKeyGroups).sort().map((root) => ({
                            label: root,
                            options: [
                              { value: `root:${root}`, label: `${root} (All Modes)` },
                              ...[...sessionItemKeyGroups[root]].sort().map((k) => ({ value: k, label: k }))
                            ]
                          }))
                        ]}
                        placeholder="All keys"
                        active={Boolean(sessionItemFilterKey)}
                      />
                    )}
                  </>
                )}
              />
            </div>
          )}
        </div>

        <DetailTimestampsFooter
          createdAt={selectedSession.createdAt}
          updatedAt={selectedSession.updatedAt}
          lastViewedAt={selectedSession.lastViewedAt}
          createdFallback={selectedSession.startedAt || selectedSession.timestamp}
          updatedFallback={selectedSession.endedAt || selectedSession.startedAt || selectedSession.timestamp}
        />

        <SendExportModal
          open={!!swipeSendTarget}
          title={swipeSendTarget ? (swipeSendTarget.location || new Date(swipeSendTarget.startedAt).toLocaleDateString() || 'Session') : ''}
          onSend={handleSwipeSendSession}
          onClose={() => setSwipeSendTarget(null)}
          user={user}
          type="session"
          item={swipeSendTarget}
          tunes={[]}
        />

        <div style={{ textAlign: 'center', marginTop: '2rem', paddingBottom: '0.5rem' }}>
          <button
            className="btn-subtle"
            onClick={() => setConfirmDeleteSession(true)}
            style={{ color: '#e03131', fontSize: '0.85rem', opacity: 0.7 }}
          >
            🗑 Delete Session
          </button>
        </div>

        <ActionConfirmModal
          open={confirmDeleteSession}
          title="Delete Session"
          message={`Permanently delete this session? This cannot be undone.`}
          confirmLabel="Delete"
          cancelLabel="Cancel"
          danger
          onConfirm={async () => {
            setConfirmDeleteSession(false);
            setSelectedSession(null);
            await swipeDeleteSession(selectedSession);
          }}
          onCancel={() => setConfirmDeleteSession(false)}
        />
      </PageWrapperAuto>
    );
  }

  return (
    <PageWrapper>
{notice && (
  <div style={{
    margin: '0.5rem auto 0.75rem',
    maxWidth: '680px',
    padding: '8px 12px',
    borderRadius: '8px',
    textAlign: 'center',
    backgroundColor: notice.tone === 'error' ? '#5a1f1f' : '#1f3f5a',
    color: '#fff',
    fontSize: '0.9rem'
  }}>
    {notice.text}
  </div>
)}




<BorderedBox
  style={{ marginBottom: 'var(--ui-section-gap)', paddingTop: 'var(--ui-panel-padding-top)' }}
  collapsible
  scrollWhenExpanded
  expandedMaxHeight="50vh"
  defaultVisible
  exposeCollapsed={() => setTimeout(updateScrollHeight, 200)}
>

  <div className="app-title ui-density-title" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
    <img src={myIcon} alt="Logo" className="responsive-icon" />
    <span>{labels.navSessions}</span>
    <FaQuestionCircle
      onClick={() => navigate('/help#sessions')}
      style={{ fontSize: '0.65em', cursor: 'pointer', color: 'var(--text-link, #228be6)', opacity: 0.8 }}
      title="Help & Tutorials"
    />
  </div>
  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px' }}>
    <ModeSwitcher inline />
  </div>

<div style={{ textAlign: 'center', marginBottom: '0.75rem' }}>
  {activeSession ? (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--ui-filter-grid-gap)', flexWrap: 'wrap', background: 'var(--input-bg)', borderRadius: '10px', padding: '8px 12px' }}>
      <span style={{ fontSize: 'var(--ui-control-font)' }}>
        Active: <strong>{activeSession.location}</strong> since {new Date(activeSession.startedAt).toLocaleString(undefined, { timeStyle: 'short' })}
      </span>
      <button className="btn-subtle" onClick={() => setShowEndSessionModal(true)} style={{ padding: '4px 12px', fontSize: 'var(--ui-compact-font)' }}>
        End Session
      </button>
    </div>
  ) : (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--ui-filter-grid-gap)', flexWrap: 'wrap' }}>
      <input
        className="search-input"
        placeholder="Location/Name (optional)"
        value={locationInput}
        onChange={e => setLocationInput(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && startSession(currentMode === 'all' ? 'tunes' : currentMode)}
        style={{
          width: 'clamp(210px, 34vw, 300px)',
          gridColumn: 'auto',
          borderColor: 'color-mix(in srgb, var(--text-link, #228be6) 30%, var(--card-border) 70%)',
          boxShadow: 'inset 0 1px 0 color-mix(in srgb, #ffffff 8%, transparent), 0 1px 4px color-mix(in srgb, #228be6 8%, transparent)'
        }}
      />
      {currentMode === 'all' ? (
        <>
          <button
            className="btn-subtle search-control-btn detail-header-pill-editing"
            onClick={() => startSession('tunes')}
            style={{
              padding: '6px 16px',
              fontWeight: '600',
              fontSize: 'var(--ui-compact-font)',
              backgroundColor: 'color-mix(in srgb, #228be6 28%, transparent)',
              border: '1px solid color-mix(in srgb, #228be6 62%, transparent)',
              color: 'var(--text-primary)'
            }}
          >
            Start Tune Session
          </button>
          <button
            className="btn-subtle search-control-btn detail-header-pill-editing"
            onClick={() => startSession('notes')}
            style={{
              padding: '6px 16px',
              fontWeight: '600',
              fontSize: 'var(--ui-compact-font)',
              backgroundColor: 'color-mix(in srgb, #d97706 28%, transparent)',
              border: '1px solid color-mix(in srgb, #d97706 62%, transparent)',
              color: 'var(--text-primary)'
            }}
          >
            Start Note Session
          </button>
        </>
      ) : (
        <button
          className="btn-subtle search-control-btn detail-header-pill-editing"
          onClick={() => startSession(currentMode)}
          style={{
            padding: '6px 16px',
            fontWeight: '600',
            fontSize: 'var(--ui-compact-font)',
            backgroundColor: 'color-mix(in srgb, #9c36b5 28%, transparent)',
            border: '1px solid color-mix(in srgb, #9c36b5 62%, transparent)',
            color: 'var(--text-primary)'
          }}
        >
          Start Session
        </button>
      )}
    </div>
  )}
</div>

<ActionConfirmModal
  open={showEndSessionModal}
  title="End Session"
  message="End the current session?"
  confirmLabel="End Session"
  cancelLabel="Keep Session"
  danger
  onConfirm={async () => {
    setShowEndSessionModal(false);
    await endSession();
  }}
  onCancel={() => setShowEndSessionModal(false)}
/>

<ActionConfirmModal
  open={showDeleteSelectedModal}
  title="Delete Sessions"
  message={`Delete ${selectedSessionKeys.length} selected session${selectedSessionKeys.length > 1 ? 's' : ''}?`}
  confirmLabel="Delete"
  cancelLabel="Cancel"
  danger
  onConfirm={async () => {
    setShowDeleteSelectedModal(false);
    await deleteSelectedSessions();
  }}
  onCancel={() => setShowDeleteSelectedModal(false)}
/>

<div style={{ maxWidth: '600px', margin: '0 auto' }}>
  <div
    className="ui-density-grid search-filter-grid"
    style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
      maxWidth: '750px',
      alignItems: 'center',
    }}
  >
    <input
      className="search-input"
      placeholder="Search title or any metadata..."
      value={filterQuery}
      onChange={(e) => setFilterQuery(e.target.value)}
      style={{ ...inputStyle, gridColumn: '1 / -1' }}
    />

    <div
      style={{
        gridColumn: '1 / -1',
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: 'var(--ui-filter-grid-gap)',
        alignItems: 'center'
      }}
    >
      <AppSelect
        className="search-core-select"
        value={filterGenre || ''}
        onChange={setFilterGenre}
        options={modernGenreOptions}
        placeholder={`All ${labels?.genreLabelPlural || 'Genres'}`}
        active={Boolean(filterGenre)}
      />

      <button
        className="btn-subtle btn-clear-filters search-control-btn search-clear-btn"
        onClick={() => {
          setFilterQuery('');
          setFilterGenre('');
          setSelectedTags([]);
          setTagLogic('AND');
          setShowTagFilterUI(false);
          setFilterTimeRange('');
          setFilterTimeBasis('updated');
          setFilterTimeMonth('');
          setFilterTimeYear('');
          setShowTimeFilters(false);
        }}
        disabled={!hasActiveSessionFilters}
      >
        {typeof window !== 'undefined' && window.innerWidth <= 600 ? 'Clear' : '✕ Clear Filters'}
      </button>
    </div>

    <div
      style={{
        gridColumn: '1 / -1',
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: 'var(--ui-filter-grid-gap)',
        alignItems: 'center'
      }}
    >
      <button
        className={`btn-subtle search-control-btn search-time-btn ${hasActiveTimeFilter ? 'search-control-active' : ''} ${showTimeFilters ? 'panel-open' : ''}`}
        onClick={() => setShowTimeFilters(v => !v)}
      >
        {showTimeFilters ? '🕒 Hide Time' : `🕒 ${timeSummaryLabel}`}
      </button>

      <button
        className={`btn-subtle search-control-btn search-advanced-btn ${selectedTags.length > 0 ? 'search-control-active' : ''} ${showTagFilterUI ? 'panel-open' : ''}`}
        onClick={() => setShowTagFilterUI(v => !v)}
      >
        {showTagFilterUI
          ? `🏷 Hide Tags${selectedTags.length > 0 ? ` (${selectedTags.length})` : ''}`
          : `🏷 Tags${selectedTags.length > 0 ? ` (${selectedTags.length})` : ''}`}
      </button>
    </div>

{showTimeFilters && (
  <TimeFilterPanel
    isMobileFilterLayout={typeof window !== 'undefined' ? window.innerWidth <= 600 : false}
    uiSize={activeUiSize}
    basisOptions={timeBasisOptions}
    selectedBasis={filterTimeBasis}
    onSelectBasis={setFilterTimeBasis}
    rangeOptions={timeRangeOptions}
    selectedRange={filterTimeRange}
    onSelectRange={handleSessionTimeRangeSelect}
    hasActiveTimeFilter={hasActiveTimeFilter}
    onClear={() => {
      setFilterTimeRange('');
      setFilterTimeBasis('updated');
      setFilterTimeMonth('');
      setFilterTimeYear('');
    }}
    monthPickerYear={monthPickerYear}
    setMonthPickerYear={setMonthPickerYear}
    selectedMonth={filterTimeMonth}
    onSelectMonth={setFilterTimeMonth}
    selectedYear={filterTimeYear}
    onSelectYear={setFilterTimeYear}
  />
)}

{showTagFilterUI && availableSessionTags.length > 0 && (
  <div style={{ gridColumn: '1 / -1' }}>
    <TagFilterControl
      availableTags={availableSessionTags}
      selectedTags={selectedTags}
      setSelectedTags={setSelectedTags}
      tagLogic={tagLogic}
      setTagLogic={setTagLogic}
    />
  </div>
)}

    <div className="search-actions-wrap" style={{ gridColumn: '1 / -1', width: '100%', maxWidth: '600px', margin: '0 auto' }}>
      <div className="tune-toolbar">
        <div
          className="tune-toolbar-row search-actions-row"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: 'var(--ui-filter-grid-gap)',
            alignItems: 'center'
          }}
        >
          <AppSelect
            className="compact-action-select search-sort-select"
            value={sortOrder}
            onChange={setSortOrder}
            options={modernSortOptions}
            placeholder="Sort"
            active={Boolean(sortOrder && sortOrder !== 'newest')}
          />

          <button
            className={`btn-subtle compact-action-btn search-control-btn search-selectall-btn ${selectedSessionKeys.length > 0 ? 'search-control-active' : ''}`}
            onClick={toggleSelectAll}
          >
            {allVisibleSessionsSelected ? 'Deselect All' : 'Select All'}
          </button>

          <AppSelect
            className={`bulk-actions-select search-bulk-select ${selectedSessionKeys.length > 0 ? 'bulk-ready control-ready-glow' : ''}`}
            value={bulkActionValue}
            onChange={(value) => {
              if (!value || selectedSessionKeys.length === 0) return;
              setBulkActionValue('');
              if (value === 'delete') {
                setShowDeleteSelectedModal(true);
              } else if (value === 'addtags') {
                setBulkTagInputVisible(true);
              } else if (value === 'action:send') {
                setShowBulkSendPicker(true);
              } else if (value === 'action:export') {
                setShowBulkExportModal(true);
              }
            }}
            placeholder={
              selectedSessionKeys.length > 0
                ? `Bulk Actions (${selectedSessionKeys.length} selected)`
                : 'Bulk Actions'
            }
            active={selectedSessionKeys.length > 0}
            options={modernBulkActionOptions}
          />
        </div>
      </div>
    </div>
  </div>
</div>




{bulkTagInputVisible && (
  <div
    style={{
      marginTop: '0.5rem',
      padding: '0.5rem',
      borderTop: '1px solid var(--border)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    }}
  >
    <p style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>
      Add tags to selected sessions:
    </p>

    <div
      style={{
        width: '100%',
        maxWidth: '400px',
        boxSizing: 'border-box',
        padding: '0 1rem',
      }}
    >
      <CustomTagInput
        tags={bulkTags}
        setTags={setBulkTags}
        suggestions={availableSessionTags}
        inputStyle={inputStyle}
      />
    </div>

    <div style={{ marginTop: '0.75rem', display: 'flex', gap: '1rem' }}>
      <button
        onClick={async () => {
          for (const id of selectedSessionKeys) {
            const session = sessions.find(s => s.id === id);
            const existing = session?.customTags || [];
            const final = Array.from(new Set([...existing, ...bulkTags]));
            if (navigator.onLine) {
              await updateDoc(doc(db, 'users', user.uid, 'sessions', id), { customTags: final });
            } else {
              await dexie.sessions.update(id, { customTags: final });
              await dexie.pendingWrites.put({
                id: `${id}__tagUpdate`,
                type: 'update',
                collection: 'sessions',
                docId: id,
                data: { customTags: final }
              });
            }
          }
          setBulkTags([]);
          setBulkTagInputVisible(false);
          showNotice(`Tags added to ${selectedSessionKeys.length} selected sessions.`);
          setSelectedSessionKeys([]);
        }}
        style={{
          padding: '6px 12px',
          fontSize: '0.8rem',
          borderRadius: '6px',
          backgroundColor: '#228be6',
          color: 'white'
        }}
      >
        Apply Tags
      </button>

      <button
        onClick={() => {
          setBulkTags([]);
          setBulkTagInputVisible(false);
        }}
        style={{ fontSize: '0.8rem' }}
      >
        Cancel
      </button>
    </div>
  </div>
)}



</BorderedBox>

{prefs?.showFieldHints && (
  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontStyle: 'italic', textAlign: 'center', margin: '0.5rem auto', maxWidth: '500px', padding: '0 10px', boxSizing: 'border-box' }}>
    Filter sessions by genre, location, or date range. Sessions log what you played and when.
    <div style={{ fontSize: '0.7rem', marginTop: '0.25rem', opacity: 0.7 }}>
      <span onClick={() => navigate('/help')} style={{ cursor: 'pointer', textDecoration: 'underline' }}>Toggle hints in Help &amp; Tutorials</span>
    </div>
  </div>
)}

{pendingDelete && (
  <UndoBanner
    className="undo-banner-inline"
    message={`${(pendingDelete.sessionKeys || []).length === 1 ? 'Session' : `${(pendingDelete.sessionKeys || []).length} sessions`} deleted.`}
    onUndo={undoDelete}
  />
)}


<div
  ref={resultsContainerRef}
  style={{
    overflowY: 'auto',
    boxSizing: 'border-box',
    height: scrollAreaHeight,
    paddingRight: '0px',
    paddingLeft: '0px',
    marginBottom: '2rem',
  }}
>

  {sessions.length === 0 ? (
    <div style={{ textAlign: 'center', padding: '2rem 1rem', lineHeight: '1.8' }}>
      <p style={{ fontSize: '1.1em', marginBottom: '0.5rem' }}>No sessions recorded yet</p>
      <p>Sessions let you {labels.sessionDescription}. Start a session, give it a location (e.g. {labels.sessionLocationHint}), and any {labels.items} you add or open can be tagged with it.</p>
      <p style={{ fontSize: '0.9em', opacity: 0.8 }}>Tap <strong>Start Session</strong> above to begin. Over time, your session history becomes {labels.sessionHistoryLabel}.</p>
      <p style={{ fontSize: '0.9em', opacity: 0.8 }}>Check out <a href="/help" style={{ color: 'var(--text-primary)' }}>Help &amp; Tutorials</a> for more tips.</p>
    </div>
  ) : (
    <ul className="card-list">
  {filteredSessions.filter(s => !pendingDelete?.sessionKeys?.includes(s.id)).map((s) => (
      (() => {
        const sessionTags = Array.isArray(s.customTags) ? s.customTags : [];
        const previewTags = selectedTags.length > 0
          ? sessionTags.filter(tag => selectedTags.includes(tag))
          : sessionTags;
        const showSessionTagPreview = prefs?.showTagPreviewInSessions !== false;
        const showSessionDatePreview = prefs?.showSessionDateInSessions !== false;
        return (
        <SwipeablePinnableRow
          key={s.id}
          pinned={s.pinned}
          disabled={activeSession?.id === s.id}
          actions={[
            { icon: '📤', label: 'Send / Export', color: '#1c7ed6', onAction: () => setSwipeSendTarget(s) },
            { icon: '🗑', label: 'Delete', color: '#e03131', onAction: () => swipeDeleteSession(s) },
            { icon: s.pinned ? '📌' : '📍', label: s.pinned ? 'Unpin' : 'Pin', color: '#e07622', onAction: () => swipePinSession(s) },
          ]}
        >
        <TuneCardBox
          onClick={() => {
            openSessionDetail(s);
          }}
        >
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '1rem'
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                flex: '1 1 200px',
                minWidth: '160px'
              }}
            >
              <StyledCheckbox
                checked={selectedSessionKeys.includes(s.id)}
                onChange={() => toggleSessionSelection(s.id)}
                onClick={(e) => e.stopPropagation()}
              />

              <div>
                <div
                  className="preview-title-row"
                >
                  <strong style={{ fontSize: '1rem' }}>
                    {s.location || s.startedAt.toLocaleString()}
                    {activeSession?.id === s.id && (
                      <span style={{ fontStyle: 'italic', color: 'green', marginLeft: '0.5ch' }}>
                        (currently active)
                      </span>
                    )}
                  </strong>
                  {currentMode === 'all' && (
                    <PreviewModePill variant={(s.mode || 'tunes') === 'notes' ? 'note' : 'tune'}>
                      {(s.mode || 'tunes') === 'notes' ? 'Notes' : 'Tunes'}
                    </PreviewModePill>
                  )}
                </div>
                {(showSessionDatePreview || s._matchedTuneTitle) && (
                <div
                  style={{
                    fontSize: '0.9rem',
                    color: 'var(--text-muted)',
                    marginTop: '2px'
                  }}
                >
         {s._matchedTuneTitle && (
<div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '4px' }}>
  🎯 Includes tune: <strong>{s._matchedTuneTitle}</strong>
</div>

)}
        
        {showSessionDatePreview && (() => {
  const startedLabel = formatDateTimeNoSeconds(s.startedAt || s.timestamp);
  const openDurationLabel = formatDurationLabel(getSessionTotalOpenMinutes(s));
  if (!s.endedAt) return `${startedLabel} — (ongoing) • Total duration ${openDurationLabel}`;
  return `${startedLabel} — Total duration ${openDurationLabel}`;
})()}



                </div>
                )}
              </div>
            </div>

            {showSessionTagPreview && previewTags.length > 0 && (
              <div style={{ flex: '0 0 auto', display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: '4px', maxWidth: '180px' }}>
                {previewTags.slice(0, 6).map((tag, idx) => (
                  (() => {
                    const normalizedTag = normalizeTag(tag || '');
                    const normalizedQuery = normalizeTag((filterQuery || '').trim());
                    const active = normalizeTagsArray(selectedTags).includes(normalizedTag) || (
                      Boolean(normalizedQuery) && normalizedTag.includes(normalizedQuery)
                    );
                    return (
                      <button
                        key={`${s.id}-session-tag-${idx}`}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          addSessionTagFromPreview(tag);
                        }}
                        className="btn-subtle"
                        style={{
                          fontSize: 'var(--ui-card-chip-font)',
                          padding: '1px 6px',
                          borderRadius: '10px',
                          lineHeight: 1.15,
                          whiteSpace: 'nowrap',
                          backgroundColor: active
                            ? 'color-mix(in srgb, var(--text-link, #228be6) 20%, transparent)'
                            : 'color-mix(in srgb, var(--text-muted) 16%, transparent)',
                          border: active
                            ? '1px solid color-mix(in srgb, var(--text-link, #228be6) 55%, transparent)'
                            : '1px solid transparent',
                          color: 'var(--text-primary)',
                          textDecoration: 'underline',
                        }}
                      >
                        {tag}
                      </button>
                    );
                  })()
                ))}
              </div>
            )}
          </div>
        </TuneCardBox>
        </SwipeablePinnableRow>
      );})()
      ))}
    </ul>
  )}
</div>


      <SendExportModal
        open={!!swipeSendTarget}
        title={swipeSendTarget ? (swipeSendTarget.location || new Date(swipeSendTarget.startedAt).toLocaleDateString() || 'Session') : ''}
        onSend={handleSwipeSendSession}
        onClose={() => setSwipeSendTarget(null)}
        user={user}
        type="session"
        item={swipeSendTarget}
        tunes={[]}
      />

      <SendExportModal
        open={showBulkSendPicker}
        onClose={() => setShowBulkSendPicker(false)}
        title={`${selectedSessionKeys.length} session${selectedSessionKeys.length !== 1 ? 's' : ''}`}
        user={user}
        onSend={handleBulkSendSessions}
        type="bulk"
        item={null}
        tunes={sessions.filter(s => selectedSessionKeys.includes(s.id))}
        defaultTab="send"
      />

      <ExportModal
        open={showBulkExportModal}
        onClose={() => setShowBulkExportModal(false)}
        type="bulk"
        item={null}
        tunes={sessions.filter(s => selectedSessionKeys.includes(s.id))}
      />

    </PageWrapper>
  );
};

export default SessionViewer;
